import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';
import Navigation from './components/Navigation';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Loading component for Suspense fallback
const LoadingComponent = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    <p className="ml-3 text-gray-700">Loading...</p>
  </div>
);

// Lazily load components that actually exist
const Login = lazy(() => import('./components/auth/Login'));
const Register = lazy(() => import('./components/auth/Register'));
const SubscriptionPlans = lazy(() => import('./components/SubscriptionPlans'));

// Legal components - NEW ADDITIONS
const TermsOfService = lazy(() => import('./components/legal/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./components/legal/PrivacyPolicy'));
const CilsLeadForm = lazy(() => import('./components/np/leadforms/CILsLeadForm'));
const CilsWomenLeadForm = lazy(() => import('./components/np/leadforms/CILsWomenLeadForm'));
const WomensUnpaidLabor = lazy(() => import('./components/np/leadforms/WomensUnpaidLabor'));
const AiWomenTwinCoachIntro = lazy(() => import('./components/np/leadforms/AiWomenTwinCoachIntro'));


// NP components
const SecureNPPlanner = lazy(() => import('./components/np/SecureNPPlanner'));
const NPSecurityLeadForm = lazy(() => import('./components/np/leadforms/NPSecurityLeadForm'));
const NonProfitEconomicsPlanner = lazy(() => import('./components/np/NonProfitEconomicsPlanner'));
const NPEconomicsDataProductPage = lazy(() => import('./components/np/NPEconomicsDataProductPage'));
const NPProspecting = lazy(() => import('./components/np/NPProspecting'));

// Impact products
const DataSecurity = lazy(() => import('./components/np/impact_products/DataSecurity'));
const DonorDemographics = lazy(() => import('./components/np/impact_products/DonorDemographics'));
const EconomicCycles = lazy(() => import('./components/np/impact_products/EconomicCycles'));
const EconomicsUtils = lazy(() => import('./components/np/impact_products/EconomicsUtils'));
const EmailSecurity = lazy(() => import('./components/np/impact_products/EmailSecurity'));
const FileSecurity = lazy(() => import('./components/np/impact_products/FileSecurity'));
const FinancialSecurity = lazy(() => import('./components/np/impact_products/FinancialSecurity'));
const FundraisingStrategies = lazy(() => import('./components/np/impact_products/FundraisingStrategies'));
const GovernmentFunding = lazy(() => import('./components/np/impact_products/GovernmentFunding'));
const InformationSecurity = lazy(() => import('./components/np/impact_products/InformationSecurity'));
const OperationalSecurity = lazy(() => import('./components/np/impact_products/OperationalSecurity'));
const ResiliencePlaybook = lazy(() => import('./components/np/impact_products/ResiliencePlaybook'));

// Wrapper component to handle conditional rendering of navigation
const AppLayout = ({ children }) => {
  const { user } = useAuth();
  return (
    <>
      {user && <Navigation />}
      <main className={user ? 'container mx-auto px-4 py-8' : ''}>
        {children}
        <footer className="bg-gray-700 text-white p-3 text-center text-sm mt-6 rounded-lg">
          <p>© {new Date().getFullYear()} AiNonProfitCoach Platform | All Rights Reserved</p>
        </footer>
      </main>
    </>
  );
};

// Protected landing page component that requires authentication
const LandingPage = () => {
  const { user } = useAuth();
  
  return (
    <div className="text-center">
      <h1 className="text-4xl font-bold mb-4">Welcome to AI Non-Profit Coach</h1>
      <p className="text-lg">Your AI-powered coaching platform for non-profit organizations.</p>
      {user && (
        <div className="mt-6">
          <p className="text-gray-600">Welcome back, {user.firstName || user.email}!</p>
        </div>
      )}
    </div>
  );
};

// Root route component that handles authentication redirect
const RootRoute = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="ml-3 text-gray-700">Loading...</p>
      </div>
    );
  }
  
  if (!user) {
    //return <Navigate to="/login" replace />;
    //return <Navigate to="/np/nonprofit-security-lead-form" replace />;
    return <Navigate to="/np/cils-lead-form" replace />;

  }
  
  return (
    <AppLayout>
      <LandingPage />
    </AppLayout>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Suspense fallback={<LoadingComponent />}>
            <Routes>
              <Route path="/" element={<RootRoute />} />
              <Route path="/login" element={<AppLayout><Login /></AppLayout>} />
              <Route path="/register" element={<AppLayout><Register /></AppLayout>} />
              
              {/* Legal Routes - NEW ADDITIONS - Public Access */}
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              
              <Route path="/subscriptionPlans" element={<ProtectedRoute><AppLayout><SubscriptionPlans /></AppLayout></ProtectedRoute>} />
              <Route path="/np/secure-np-planner" element={<ProtectedRoute><AppLayout><SecureNPPlanner /></AppLayout></ProtectedRoute>} />
              <Route path="/np/np-economics-planner" element={<ProtectedRoute><AppLayout><NonProfitEconomicsPlanner /></AppLayout></ProtectedRoute>} />
              <Route path="/np/np-prospecting" element={<ProtectedRoute><AppLayout><NPProspecting /></AppLayout></ProtectedRoute>} />
              <Route path="/np/cils-lead-form" element={<AppLayout><CilsLeadForm /></AppLayout>} />           
              <Route path="/np/cils-women-lead-form" element={<AppLayout><CilsWomenLeadForm /></AppLayout>} />           
              <Route path="/np/womens-unpaid-labor" element={<AppLayout><WomensUnpaidLabor /></AppLayout>} />           
              <Route path="/np/ai-women-twin-coach-intro" element={<AppLayout><AiWomenTwinCoachIntro /></AppLayout>} />           
             
              <Route path="/np/nonprofit-security-lead-form" element={<AppLayout><NPSecurityLeadForm /></AppLayout>} />
              <Route path="/np/np-economics-data-product-page" element={<ProtectedRoute><AppLayout><NPEconomicsDataProductPage /></AppLayout></ProtectedRoute>} /> 
              <Route path="/np/impact-products/data-security" element={<ProtectedRoute><AppLayout><DataSecurity /></AppLayout></ProtectedRoute>} />
              <Route path="/np/impact-products/donor-demographics" element={<ProtectedRoute><AppLayout><DonorDemographics /></AppLayout></ProtectedRoute>} />
              <Route path="/np/impact-products/economic-cycles" element={<ProtectedRoute><AppLayout><EconomicCycles /></AppLayout></ProtectedRoute>} />
              <Route path="/np/impact-products/economics-utils" element={<ProtectedRoute><AppLayout><EconomicsUtils /></AppLayout></ProtectedRoute>} />
              <Route path="/np/impact-products/email-security" element={<ProtectedRoute><AppLayout><EmailSecurity /></AppLayout></ProtectedRoute>} />
              <Route path="/np/impact-products/file-security" element={<ProtectedRoute><AppLayout><FileSecurity /></AppLayout></ProtectedRoute>} />
              <Route path="/np/impact-products/financial-security" element={<ProtectedRoute><AppLayout><FinancialSecurity /></AppLayout></ProtectedRoute>} />
              <Route path="/np/impact-products/fundraising-strategies" element={<ProtectedRoute><AppLayout><FundraisingStrategies /></AppLayout></ProtectedRoute>} />
              <Route path="/np/impact-products/government-funding" element={<ProtectedRoute><AppLayout><GovernmentFunding /></AppLayout></ProtectedRoute>} />
              <Route path="/np/impact-products/information-security" element={<ProtectedRoute><AppLayout><InformationSecurity /></AppLayout></ProtectedRoute>} />
              <Route path="/np/impact-products/operational-security" element={<ProtectedRoute><AppLayout><OperationalSecurity /></AppLayout></ProtectedRoute>} />
              <Route path="/np/impact-products/resilience-playbook" element={<ProtectedRoute><AppLayout><ResiliencePlaybook /></AppLayout></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;